// Магазин — за макетом «Gamification Screens», кадри «Магазин · за монети»
// й «Магазин · за кавові боби». Перемикач валюти, розділи, плитки й
// однорядкові списки: структура з макета, вміст — із /shop.
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { LoadFailed } from "../ui/Net.jsx";
import { Coins2 } from "../ui/Coins.jsx";
import { ItemIcon } from "../ui/ItemIcon.jsx";
import { webp } from "../ui/img.jsx";
import { feature } from "../features.js";

const Bean = ({ size = 18 }) => (
  <img src="/assets/ui/bean.webp" alt="боби" style={{ width: size, height: size + 2 }} />
);

// Розміри картинок — з макета: у кожної свої пропорції, і «вписати в
// квадрат» дає не ті пікселі.
const ART = {
  water: [30, 33], compost: [24, 35], fertilizer: [19, 36], insecticide: [22, 36],
  sapling: [22, 34], sapling_beans: [22, 34], pos_discount: [26, 35], beans_to_coins: [32, 34],
  coffee_250g: [44, 58], merch_cup: [48, 58], custom_print: [56, 56],
};
const Art = ({ item }) => {
  const [width, height] = ART[item.code] ?? [];
  return <img src={webp(`/${item.icon}`)} alt="" style={width ? { width, height, maxHeight: "none" } : undefined} />;
};

const price = (item) => (item.price_range ? item.price_range.join("-") : item.price);

// Плитка товару: на відміну від вітрини одягу картинка не тягнеться на
// всю висоту — вміст притиснутий догори, як у макеті.
function Tile({ item, onOpen }) {
  return (
    <button className="card-item" onClick={() => onOpen(item)}>
      <Art item={item} />
      <span className="name">{item.title}</span>
      <span className="cost"><Bean />{price(item)}</span>
    </button>
  );
}

// Рядок догляду: одна лінія «Вода · 5 л» і ціна в колонці 78 px.
function CareRow({ item, onOpen }) {
  return (
    <button className="list-row" onClick={() => onOpen(item)}>
      <span className="ico"><Art item={item} /></span>
      <span className="name">{item.unit ? `${item.title} (${item.unit})` : item.title}</span>
      <span className="cost"><Coins2 size={17} overlap={6} />{item.price}</span>
    </button>
  );
}

// Рядок за боби: назва з поясненням під нею й ціна праворуч; обмін
// замість ціни показує курс і шеврон.
function BeanRow({ item, onOpen }) {
  const exchange = item.kind === "exchange";
  return (
    <button className="list-row two" onClick={() => onOpen(item)}>
      <span className="ico"><Art item={item} /></span>
      <span className="name">
        {item.title}
        {exchange ? (
          <small className="rate">1 <img src="/assets/ui/bean.webp" alt="боб" /> → {item.gives_coins} <img src="/assets/ui/coin_gold.webp" alt="золотих монет" /></small>
        ) : (
          <small>{item.subtitle}</small>
        )}
      </span>
      {item.available === false ? (
        <span className="soon">скоро</span>
      ) : exchange ? (
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" style={{ flex: "none" }}>
          <path d="M9.5 6 15.5 12 9.5 18" />
        </svg>
      ) : (
        <span className="cost-r"><Bean />{item.price}</span>
      )}
    </button>
  );
}

// Обрана вкладка магазину («За монети» / «За зерна») і коли її обрали.
const MODE_KEY = "extrovert.shop";
const MODE_TTL_MS = 60 * 60 * 1000;

function shopMode() {
  try {
    const saved = JSON.parse(localStorage.getItem(MODE_KEY));
    return saved && Date.now() - saved.at < MODE_TTL_MS ? saved.mode : "coins";
  } catch {
    return "coins";
  }
}

export function rememberShopMode(mode) {
  try { localStorage.setItem(MODE_KEY, JSON.stringify({ mode, at: Date.now() })); } catch { /* приватний режим */ }
}

export function Shop({ ctx }) {
  const [shop, setShop] = useState(() => api.peek("/shop") ?? null);
  const [clothes, setClothes] = useState(() => api.peek("/catalog/featured")?.items ?? []);
  // Вкладка переживає перехід на інший екран і перезавантаження: людина
  // купує за зерна й повертається саме за зерна (скарга власника
  // 23.09.2026). Але лише годину від вибору (власник, 01.10.2026): наступного
  // дня магазин знову відкривається на монетах.
  const [mode, setMode] = useState(shopMode);
  const pick = (next) => { setMode(next); rememberShopMode(next); };

  const [failed, setFailed] = useState(false);
  const load = () => api.get("/shop").then((s) => { setFailed(false); setShop(s); }).catch(() => setFailed(true));
  useEffect(() => {
    load();
    // Три речі на вітрині — добова випадкова трійка з сервера
    // (/catalog/featured): однакова для всіх до київської півночі.
    api.get("/catalog/featured").then((r) => setClothes(r.items ?? [])).catch(() => {});
  }, []);

  const open = (item) => {
    if (item.code === "clothing") return ctx.push("catalog", { visit: Date.now() });
    ctx.push(item.kind === "crate" ? "shopItem" : "shopProduct", { item });
  };

  const byCode = (list, code) => list?.find((i) => i.code === code);
  const coins = shop?.coins ?? [];
  const beans = shop?.beans ?? [];
  const crate = byCode(coins, "crate");
  const care = coins.filter((i) => i.kind === "care");
  const sapling = byCode(coins, "sapling");
  const delivery = beans.filter((i) => i.kind === "delivery");
  const onPoint = beans.filter((i) => i.kind === "discount");
  const inGame = beans.filter((i) => i.kind === "sapling" || i.kind === "exchange");
  const orders = ctx.me?.badges?.orders ?? 0;

  return (
    <div className="stage-pad">
      <div className="row">
        <div className="seg">
          <button data-on={mode === "coins"} onClick={() => pick("coins")}>
            За <Coins2 />
          </button>
          <button data-on={mode === "beans"} onClick={() => pick("beans")}>
            За <Bean size={15} />
          </button>
        </div>
        <button className="chip-btn" onClick={() => ctx.push("orders")}>
          Мої замовлення
          {orders > 0 && <i>{orders}</i>}
        </button>
      </div>

      {!shop && (failed ? <LoadFailed onRetry={() => { setFailed(false); load(); }} /> : <div className="skeleton" />)}

      {shop && mode === "coins" && (
        <>
          <div className="section">
            <div className="section-head">
              <div className="sectionTitle">Сьогодні у моді</div>
              <button className="link-more" data-tap="off" onClick={() => ctx.push("catalog", { visit: Date.now() })}>
                Весь одяг
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M9.5 6 15.5 12 9.5 18" />
                </svg>
              </button>
            </div>
            <div className="cards">
              {clothes.map((it) => (
                <button key={it.code} className="card-item" onClick={() => ctx.push("itemCard", { item: it })}>
                  <span className="art"><ItemIcon sprite={it.sprite_id} size={58} style={{ width: 58 }} /></span>
                  <span className="name">{it.name}</span>
                  <span className="cost">
                    <Coins2 size={15} overlap={6} />
                    {it.price_coins}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {crate && (
            <div className="section">
              <div className="sectionTitle">Перевір удачу</div>
              <button className="crate-row" onClick={() => open(crate)}>
                <span className="crate-art">
                  <img src="/assets/ui/crate.webp" alt="щаслива скринька" />
                  <img src="/assets/ui/crate_lid.webp" alt="" />
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800 }}>{crate.title}</span>
                <span className="crate-price">
                  <div><Coins2 size={18} overlap={6} />{crate.price}</div>
                  {feature(ctx.me, "crate_cash") && crate.price_uah ? <small>або {crate.price_uah} ₴</small> : null}
                </span>
              </button>
            </div>
          )}

          {care.length > 0 && (
            <div className="section">
              <div className="sectionTitle">Догляд</div>
              <div className="list-card">
                {care.map((it) => <CareRow key={it.code} item={it} onOpen={open} />)}
                {sapling && <CareRow item={sapling} onOpen={open} />}
              </div>
            </div>
          )}
        </>
      )}

      {shop && mode === "beans" && (
        <>
          {delivery.length > 0 && (
            <div className="section">
              <div className="sectionTitle">Реальні товари</div>
              <div className="cards">
                {/* Без рамки на футболці й без плашки «тільки твій» під
                    плитками: рамка нічого не пояснювала, а «тільки твоє»
                    тепер на самих чашці й футболці (власник, 27.09.2026). */}
                {delivery.map((it) => <Tile key={it.code} item={it} onOpen={open} />)}
              </div>
            </div>
          )}

          {onPoint.length > 0 && (
            <div className="section">
              <div className="sectionTitle">На точці</div>
              <div className="list-card">
                {onPoint.map((it) => <BeanRow key={it.code} item={it} onOpen={open} />)}
              </div>
            </div>
          )}

          {inGame.length > 0 && (
            <div className="section">
              <div className="sectionTitle">У грі</div>
              <div className="list-card">
                {inGame.map((it) => <BeanRow key={it.code} item={it} onOpen={open} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
